import { isClassProvider, isFactoryProvider, isNormalToken, isTokenProvider, isValueProvider } from "./providers";
import { isTokenDescriptor } from "./providers/injection-token";
import Registry from "./registry";
export const typeInfo = new Map();
class InternalDependencyContainer {
    constructor(parent) {
        this.parent = parent;
        this._registry = new Registry();
    }
    register(token, provider, options = { singleton: false }) {
        if (options.singleton) {
            if (isValueProvider(provider) || isFactoryProvider(provider)) {
                throw "Cannot use {singleton: true} with ValueProviders or FactoryProviders";
            }
        }
        this._registry.set(token, { provider, options });
        return this;
    }
    registerType(from, to) {
        if (isNormalToken(to)) {
            return this.register(from, {
                useToken: to
            });
        }
        return this.register(from, {
            useClass: to
        });
    }
    registerInstance(token, instance) {
        return this.register(token, {
            useValue: instance
        });
    }
    registerSingleton(from, to) {
        if (isNormalToken(from)) {
            if (isNormalToken(to)) {
                return this.register(from, {
                    useToken: to
                }, { singleton: true });
            }
            else if (to) {
                return this.register(from, {
                    useClass: to
                }, { singleton: true });
            }
            throw "Cannot register a type name as a singleton without a \"to\" token";
        }
        let useClass = from;
        if (to && !isNormalToken(to)) {
            useClass = to;
        }
        return this.register(from, {
            useClass
        }, { singleton: true });
    }
    resolve(token) {
        const registration = this.getRegistration(token);
        if (!registration && isNormalToken(token)) {
            throw `Attempted to resolve unregistered dependency token: ${token.toString()}`;
        }
        if (registration) {
            return this.resolveRegistration(registration);
        }
        return this.construct(token);
    }
    resolveRegistration(registration) {
        if (isValueProvider(registration.provider)) {
            return registration.provider.useValue;
        }
        else if (isTokenProvider(registration.provider)) {
            return registration.options.singleton
                ? registration.instance ||
                    (registration.instance = this.resolve(registration.provider.useToken))
                : this.resolve(registration.provider.useToken);
        }
        else if (isClassProvider(registration.provider)) {
            return registration.options.singleton
                ? registration.instance ||
                    (registration.instance = this.construct(registration.provider.useClass))
                : this.construct(registration.provider.useClass);
        }
        else if (isFactoryProvider(registration.provider)) {
            return registration.provider.useFactory(this);
        }
        else {
            return this.construct(registration.provider);
        }
    }
    resolveAll(token) {
        const registration = this.getAllRegistrations(token);
        if (!registration && isNormalToken(token)) {
            throw `Attempted to resolve unregistered dependency token: ${token.toString()}`;
        }
        if (registration) {
            return registration.map(item => this.resolveRegistration(item));
        }
        return [this.construct(token)];
    }
    isRegistered(token) {
        return this._registry.has(token);
    }
    reset() {
        this._registry.clear();
    }
    createChildContainer() {
        return new InternalDependencyContainer(this);
    }
    getRegistration(token) {
        if (this.isRegistered(token)) {
            return this._registry.get(token);
        }
        if (this.parent) {
            return this.parent.getRegistration(token);
        }
        return null;
    }
    getAllRegistrations(token) {
        if (this.isRegistered(token)) {
            return this._registry.getAll(token);
        }
        if (this.parent) {
            return this.parent.getAllRegistrations(token);
        }
        return null;
    }
    construct(ctor) {
        if (ctor.length === 0) {
            return new ctor();
        }
        const paramInfo = typeInfo.get(ctor);
        if (!paramInfo || paramInfo.length === 0) {
            throw `TypeInfo not known for ${ctor}`;
        }
        const params = paramInfo.map(param => {
            if (isTokenDescriptor(param)) {
                return param.multiple
                    ? this.resolveAll(param.token)
                    : this.resolve(param.token);
            }
            return this.resolve(param);
        });
        return new ctor(...params);
    }
}
export const instance = new InternalDependencyContainer();
export default instance;
